import { useMutation } from '@apollo/client';
import { useMsal } from '@azure/msal-react';
import {
  LoginMutationData,
  LoginMutationVariables,
  LOGIN_MUTATION,
} from 'graphql/mutation/login.mutation';
import { NextRouter, useRouter } from 'next/router';
import React, { useEffect } from 'react';

import { useMeQuery } from '../graphql/query/me.query';
import { LoadingPage } from './loading.page';

export const clearAuth = (): void => {
  localStorage.removeItem('access');
  localStorage.removeItem('refresh');
};

export const setAuth = (access: string, refresh: string): void => {
  localStorage.setItem('access', access);
  localStorage.setItem('refresh', refresh);
};

const redirectToLogin = (router: NextRouter) => {
  clearAuth();
  const path = router.asPath || router.pathname;
  localStorage.setItem('redirect', path);
  router.push('/');
};

export const withAuth = <P,>(Component: React.ComponentType<P>) => {
  const WrappedWithAuth = (props: P) => {
    const router = useRouter();
    const { accounts } = useMsal();
    const { loading: isUserInfoLoading, data: userData } = useMeQuery();
    const [
      login,
      { data: loginData, loading: isLoginLoading, error: loginError },
    ] = useMutation<LoginMutationData, LoginMutationVariables>(LOGIN_MUTATION);

    if (loginData) {
      setAuth(loginData.tokens.access, loginData.tokens.refresh);
    }

    useEffect(() => {
      const loginUser = async () => {
        if (!accounts?.[0]?.idTokenClaims) {
          console.error('No account info found. Redirecting to login.');
          redirectToLogin(router);
          return;
        }

        try {
          // Endpoint will not accept 'sid'. Necessary to remove it for SSO to work.
          const { sid, ...withoutSid } = accounts[0].idTokenClaims;
          await login({
            variables: {
              token: withoutSid,
            },
          });
        } catch (e) {
          console.error('Login mutation error:', e);
          redirectToLogin(router);
        }
      };

      loginUser();
    }, [accounts, login, router]);

    if (!accounts?.[0]?.idTokenClaims || !!loginError) {
      if (loginError) {
        console.error('Login error:', loginError);
      }
      redirectToLogin(router);
      return null;
    }

    if (isUserInfoLoading) {
      return <LoadingPage message={'Loading Credentials'} />;
    }

    if (isLoginLoading) {
      return <LoadingPage message={'Checking Credentials'} />;
    }

    return <Component me={userData && userData.me} {...props} />;
  };

  WrappedWithAuth.displayName = `withAuth(${Component.displayName || Component.name || 'Component'})`;
  return WrappedWithAuth;
};
